import { cn } from '../../utilities/cn';
import React, { useMemo } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import * as Tooltip from '@radix-ui/react-tooltip';

// Define button styles using tv
export const buttonStyles = tv({
  base: 'flex items-center justify-center transition-colors duration-300 cursor-pointer',
  variants: {
    variant: {
      primary: 'text-content-lighter bg-surface-interactive border border-transparent',
      secondary: 'text-content-dark bg-surface-white border border-stroke-light',
      inverse:
        'text-surface-white bg-transparent border border-surface-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-white',
      tertiary: 'text-content-interactive bg-transparent border border-transparent',
      destructive: 'text-danger-dark bg-transparent border border-transparent',
      premiumPurple: 'text-accent-purple-fg bg-surface-white border border-accent-purple-fg focus-visible:ring-1 focus-visible:ring-accent-purple-fg',
    },
    size: {
      sm: 'rounded-radius-sm py-xs px-sm gap-xs !text-label-small',
      lg: 'rounded-radius-md py-sm px-lg gap-sm !text-label-large',
    },
    selected: {
      true: '',
      false: '',
    },
    disabled: {
      true: 'opacity-50 hover:cursor-not-allowed',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'sm',
  },

  compoundVariants: [
    // Hover State Variants
    {
      variant: 'primary',
      disabled: false,
      className: 'hover:bg-surface-interactive-dark',
    },
    {
      variant: 'secondary',
      disabled: false,
      className: 'hover:bg-surface-interactive-lighter hover:border-stroke-interactive hover:text-content-interactive',
    },
    {
      variant: 'inverse',
      disabled: false,
      className: 'hover:bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      disabled: false,
      className: 'hover:text-content-interactive hover:bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      disabled: false,
      className: 'hover:bg-danger-light',
    },
    {
      variant: 'premiumPurple',
      disabled: false,
      className: 'hover:bg-accent-purple-muted hover:border-accent-purple-fg hover:text-accent-purple-fg',
    },

    // Selected State Variants
    {
      variant: 'primary',
      selected: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive text-content-interactive',
    },
    {
      variant: 'inverse',
      selected: true,
      className: 'bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      selected: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      className: 'bg-danger-light',
    },
    {
      variant: 'premiumPurple',
      selected: true,
      className: 'bg-accent-purple-muted',
    },

    //selected and disabled state variants
    {
      variant: 'primary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-dark text-content-lighter',
    },
    {
      variant: 'secondary',
      selected: true,
      disabled: true,
      className: 'bg-surface-interactive-lighter border-stroke-interactive text-content-interactive',
    },
    {
      variant: 'inverse',
      selected: true,
      disabled: true,
      className: 'bg-surface-white/10',
    },
    {
      variant: 'tertiary',
      selected: true,
      disabled: true,
      className: 'text-content-interactive bg-surface-interactive-lighter',
    },
    {
      variant: 'destructive',
      selected: true,
      disabled: true,
      className: 'bg-danger-light',
    },
    {
      variant: 'premiumPurple',
      selected: true,
      disabled: true,
      className: 'bg-accent-purple-muted',
    },
  ],
});

// Button props extends VariantProps from tailwind-variants
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
  /**
   * When set, the button is rendered as disabled and wrapped in a Tooltip
   * with this string as content. Internally renders as a <div> to avoid the
   * "native disabled buttons don't fire mouse events" issue that breaks
   * Tooltip hover detection.
   */
  disabledReason?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, leftIcon, rightIcon, children, variant, size, disabled = false, selected = false, disabledReason, onClick, ...rest }, ref) => {
    const isDisabled = disabled || !!disabledReason;
    const variants = useMemo(
      () => buttonStyles({ variant, size, disabled: isDisabled, selected }),
      [variant, size, isDisabled, selected],
    );

    const content = (
      <>
        {leftIcon && <span>{leftIcon}</span>}
        {children}
        {rightIcon && <span>{rightIcon}</span>}
      </>
    );

    if (disabledReason) {
      return (
        <Tooltip.Provider delayDuration={200}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div role="button" aria-disabled className={cn(variants, className)}>
                {content}
              </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="px-sm py-xs rounded-radius-sm bg-surface-darker text-white text-body-small shadow-raised z-50"
                sideOffset={4}>
                {disabledReason}
                <Tooltip.Arrow className="fill-surface-darker" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      );
    }

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(variants, className)}
        onClick={isDisabled ? undefined : onClick}
        {...rest}>
        {content}
      </button>
    );
  },
);

Button.displayName = 'Button';
